/**
 * Маршрутизатор озвучки.
 *
 * Google TTS — основний рушій за замовчуванням, системний Web Speech API —
 * запасний. Вибір зберігається в налаштуваннях (`settings.ttsEngine`) і
 * передається сюди явно, щоб жоден рушій не вмикався непомітно.
 */

import * as browserTts from './browserTts.js'
import * as googleTts from './tts.js'

export const TTS_ENGINES = [
  {
    id: 'google',
    label: 'Google TTS',
    hint: 'Однакова українська озвучка у всіх браузерах',
  },
  {
    id: 'browser',
    label: 'Системний голос',
    hint: 'Старий рушій браузера, якщо Google недоступний',
  },
]

export const DEFAULT_TTS_ENGINE = 'google'

export const normalizeTtsEngine = (value) => (value === 'browser' ? 'browser' : 'google')

export const engineLabel = (value) =>
  TTS_ENGINES.find((engine) => engine.id === normalizeTtsEngine(value))?.label ?? 'Google TTS'

export function isEngineSupported(engine) {
  return normalizeTtsEngine(engine) === 'browser' ? browserTts.isSupported() : googleTts.isSupported()
}

/** Для сумісності: перевіряє саме вибраний рушій, а не будь-який доступний. */
export const isSupported = (engine) => isEngineSupported(engine)

/** Зупиняє обидва рушії, щоб вони ніколи не звучали одночасно. */
export function stop() {
  browserTts.stop()
  googleTts.stop()
}

function selectedEngine(opts = {}) {
  return normalizeTtsEngine(opts.engine ?? opts.ttsEngine ?? DEFAULT_TTS_ENGINE)
}

/**
 * Озвучує текст вибраним рушієм.
 * Google ігнорує rate/voiceName: у нього фіксований український голос.
 */
export function speak(text, opts = {}) {
  const engine = selectedEngine(opts)
  stop()
  if (engine === 'browser') return browserTts.speak(text, opts)
  return googleTts.speak(text, opts)
}

/** Озвучує слова по черзі вибраним рушієм. Повертає функцію зупинки. */
export function speakSequence(items, opts = {}) {
  const engine = selectedEngine(opts)
  stop()
  if (engine === 'browser') return browserTts.speakSequence(items, opts)
  return googleTts.speakSequence(items, opts)
}

/** Пряме відтворення одним викликом. Потрібне тестам і кнопкам без колбеків. */
export function playAudio(text, opts = {}) {
  const engine = selectedEngine(opts)
  stop()
  if (engine === 'browser') {
    return new Promise((resolve) => {
      browserTts.speak(text, {
        ...opts,
        onEnd: () => {
          opts.onEnd?.()
          resolve(true)
        },
        onError: (err) => {
          opts.onError?.(err)
          resolve(false)
        },
      })
    })
  }
  return googleTts.playAudio(text, opts)
}
